import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLanguage } from 'src/common/hooks';
import { getDashboardStatsAPI } from 'src/utils/api';
import { client } from 'src/utils/api-client';
import { OrganizationDataOutSchema } from 'src/utils/api.interfaces';
import { OrganizationTag, hasTag } from 'src/utils/tags';
import { timeConverter } from 'src/utils/timeConverter';
import { GhgFactorTableEntrySchema } from './types';
import {
  GhgSupplierSurvey,
  RegistrationRabenTokenSchema,
  GhgDataEntryCompletedStatisticsSchema,
  RabenConsentSchema,
  CommitmentTargetStateSchema,
  CommitmentTargetRowSchema,
  CommitmentTargetPreviewSchema,
  CommitmentReadinessSchema,
  DeclinePayload,
} from './interfaces';
import {
  GhgSupplierSurveySummaryWithCountry,
  GhgSupplierSurveyPagedResult,
  requestSurveys,
  unwrapSurveyList,
} from './Scope3GoodsAndServices/hooks';

// eslint-disable-next-line
export const useGhgSidebarPermissions = (
  organizationId: number,
  year: number
) => {
  const queryKey = ['ghgSidebarPermissions', { organizationId, year }];
  const query = useQuery(queryKey, () =>
    client
      .get<{ [key: string]: boolean }>(
        '/web/user-organization-permissions/ghg-sidebar',
        {
          params: { organization_id: organizationId, year },
        }
      )
      .then((res) => res.data)
  );

  const data = query.data || {};

  const hasAccess = (scope: string, section: string) =>
    data[`${scope}::${section}`] || false;

  return { data, hasAccess };
};

export const useGhgFactorEntries = () => {
  const { language } = useLanguage();
  const queryKey = ['ghgFactorEntries', language];

  const query = useQuery(queryKey, () =>
    client
      .get<GhgFactorTableEntrySchema[]>('/web/ghg-factors/factor-entries')
      .then((res) => res.data)
  );

  const data = query.data || [];

  return { ...query, data };
};

export const useSupplierSurveyOwn = (
  organizationId?: number,
  reportingPeriod?: string
) => {
  const endpointUrl = `/web/ghg-suppliers/${organizationId}/survey/own`;
  const query = useQuery(
    ['ghg-supplier', 'survey-own', { organizationId, reportingPeriod }],
    () =>
      client
        .get<GhgSupplierSurvey | null>(endpointUrl, {
          params: { reporting_period: reportingPeriod },
        })
        .then((response) => response.data),
    { enabled: organizationId !== undefined }
  );

  return query;
};

export const useCreateSupplierSurveyOwn = (
  organizationId: number,
  reportingPeriod: string
) => {
  const queryClient = useQueryClient();
  const endpointUrl = `/web/ghg-suppliers/${organizationId}/survey/own`;

  const create = useMutation({
    mutationFn: () =>
      client
        .post<GhgSupplierSurvey>(
          endpointUrl,
          {},
          { params: { reporting_period: reportingPeriod } }
        )
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['ghg-supplier', 'survey-own']);
    },
  });

  return create;
};

export const useRegistrationRabenToken = (organizationId: number) => {
  const endpointUrl = '/web/get_raben_token';
  const query = useQuery(
    ['ghg-supplier', 'raben-token', { organizationId }],
    () =>
      client
        .get<RegistrationRabenTokenSchema[]>(endpointUrl, {
          params: { organization_id: organizationId },
        })
        .then((response) => response.data)
  );

  return query;
};

export const useGhgDataEntriesCompletedStatistics = (
  organizationId: number | null,
  fromPeriod: string | null,
  toPeriod: string | null
) => {
  const query = useQuery<GhgDataEntryCompletedStatisticsSchema>(
    ['data-entries-completed', { organizationId, fromPeriod, toPeriod }],
    () =>
      client
        .get('/web/v2/data-entries-completed/statistics', {
          params: {
            organization_id: organizationId,
            from_period: fromPeriod,
            to_period: toPeriod,
          },
        })
        .then((res) => res?.data),
    {
      enabled:
        organizationId !== null && fromPeriod !== null && toPeriod !== null,
    }
  );

  const statistics = query.data;
  const scope1Finished = (statistics?.scope_1_percent || 0) >= 100;
  const scope2Finished = (statistics?.scope_2_percent || 0) >= 100;

  return { ...query, data: query.data, scope1Finished, scope2Finished };
};

// Raben 14.10: the one carrier-side rule for "Scope 1 / Scope 2 complete for
// the year". Clippy's two steps, its certificate step and the certificate in
// the sidebar menu all read it, so they cannot disagree. It means what the
// readiness endpoint means: every period of the year, in the organization's
// reporting frequency, marked as completed; for a Raben carrier Scope 1 must
// also hold data (emissions above zero for the year, Raben review 02.10).
export const useYearScope12Completion = (
  organization: OrganizationDataOutSchema,
  year: number
) => {
  const reportingFreq = organization.co2?.reporting_freq;
  const yearStart = reportingFreq
    ? timeConverter(new Date(year, 0, 1), reportingFreq)
    : null;
  const yearEnd = reportingFreq
    ? timeConverter(new Date(year, 11, 31), reportingFreq)
    : null;
  const { data: statistics } = useGhgDataEntriesCompletedStatistics(
    organization.id,
    yearStart,
    yearEnd
  );
  const isRaben = hasTag(organization, OrganizationTag.Raben);
  const { data: yearEmissions } = useQuery(
    ['ghg-year-emissions', organization.id, yearStart, yearEnd],
    () => getDashboardStatsAPI(yearStart as string, yearEnd, organization.id),
    { enabled: isRaben && !!yearStart && !!yearEnd }
  );
  const scope1HasData = (yearEmissions?.stats?.emission_scope_1 || 0) > 0;

  return {
    scope1Complete:
      (statistics?.scope_1_percent || 0) >= 100 && (!isRaben || scope1HasData),
    scope2Complete: (statistics?.scope_2_percent || 0) >= 100,
  };
};

export const useRabenConsent = (organizationId?: number) => {
  const queryClient = useQueryClient();
  const endpointUrl = `/web/ghg-suppliers/${organizationId}/consent`;
  const query = useQuery(
    ['ghg-supplier', 'raben-consent', { organizationId }],
    () => client.get<RabenConsentSchema>(endpointUrl).then((res) => res.data),
    { enabled: organizationId !== undefined }
  );

  // RG 28.08: the commitment-target endpoints answer 409 until the Letter is
  // accepted, so a Letter decision also refreshes every target query.
  const invalidateAfterDecision = () => {
    queryClient.invalidateQueries(['ghg-supplier', 'raben-consent']);
    queryClient.invalidateQueries(['ghg-supplier', 'commitment-target']);
  };

  const accept = useMutation(
    () => client.post<RabenConsentSchema>(endpointUrl, { consent: true }),
    { onSuccess: invalidateAfterDecision }
  );

  // RG 28.08 D3: the reason is optional -- called without a payload the body
  // stays the plain `{ consent: false }`.
  const decline = useMutation(
    (payload: DeclinePayload | void) =>
      client.post<RabenConsentSchema>(
        endpointUrl,
        payload
          ? {
              consent: false,
              decline_reason: payload.reason,
              decline_reason_text: payload.reasonText,
            }
          : { consent: false }
      ),
    { onSuccess: invalidateAfterDecision }
  );

  return { ...query, accept, decline };
};

// C17 -- backend/ghg_suppliers/api/api_commitment_target.py. `target` is
// null until the first set (Goal 1's "not-set" state); `eligible_years` is
// ALWAYS server-computed (BR-2) and is the only thing that ever populates
// the base-year Select -- this hook never invents or filters years itself.
export const useCommitmentTarget = (
  organizationId?: number,
  // TargetPromptModal gates this GET on consent === true (the endpoint
  // 409s for anyone else); TargetSection keeps the default since it is
  // only ever mounted post-accept.
  options: { enabled?: boolean } = {}
) => {
  const queryClient = useQueryClient();
  const endpointUrl = `/web/ghg-suppliers/${organizationId}/commitment-target`;
  const queryKey = ['ghg-supplier', 'commitment-target', { organizationId }];

  const query = useQuery(
    queryKey,
    () =>
      client
        .get<CommitmentTargetStateSchema>(endpointUrl)
        .then((res) => res.data),
    { enabled: organizationId !== undefined && (options.enabled ?? true) }
  );

  // BR-1: the only client input is `base_year` -- everything in the row it
  // resolves with (target_year/reduction_pct/emissions) is server-computed.
  // RG 28.08 D5: the caller may also send `expectedBaseEmissions`, the
  // base-emissions figure the user saw in the preview; the server answers 412
  // "stale_preview" (no write) when the data changed since. A plain number
  // is still accepted and means "just the base year".
  const setTarget = useMutation(
    (arg: number | { baseYear: number; expectedBaseEmissions?: number }) => {
      const { baseYear, expectedBaseEmissions } =
        typeof arg === 'number'
          ? { baseYear: arg, expectedBaseEmissions: undefined }
          : arg;
      const body: { base_year: number; expected_base_emissions?: number } = {
        base_year: baseYear,
      };
      if (expectedBaseEmissions !== undefined) {
        body.expected_base_emissions = expectedBaseEmissions;
      }
      return client
        .post<CommitmentTargetRowSchema>(endpointUrl, body)
        .then((res) => res.data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ghg-supplier', 'commitment-target']);
      },
    }
  );

  return { ...query, setTarget };
};

// RG 28.08 8.4, D3/D4: refuse to set the target, with an optional reason.
// Resolves with the new state (`declined` filled in); every target query is
// refreshed so the pop-up and the tab agree straight away.
export const useDeclineCommitmentTarget = (organizationId?: number) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: DeclinePayload) =>
      client
        .post<CommitmentTargetStateSchema>(
          `/web/ghg-suppliers/${organizationId}/commitment-target/decline`,
          { reason: payload.reason, reason_text: payload.reasonText }
        )
        .then((res) => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ghg-supplier', 'commitment-target']);
      },
    }
  );
};

// RG 28.08 8.4/9.1, D5: the Statement's figures for `baseYear` before the
// user accepts. Read-only on the server; disabled until both ids are known.
// The server computes them with the same code as the POST, so these numbers
// are what will be frozen on accept (unless the data changes in between,
// which the POST's `expectedBaseEmissions` guards against).
export const useCommitmentTargetPreview = (
  organizationId?: number,
  baseYear?: number
) =>
  useQuery(
    [
      'ghg-supplier',
      'commitment-target',
      'preview',
      { organizationId, baseYear },
    ],
    () =>
      client
        .get<CommitmentTargetPreviewSchema>(
          `/web/ghg-suppliers/${organizationId}/commitment-target/preview?base_year=${baseYear}`
        )
        .then((res) => res.data),
    { enabled: organizationId !== undefined && baseYear !== undefined }
  );

// RG 28.08 2.3: per-year scope 1/2 completion for the "what is missing"
// checklist. Unlike the target GET it does not 409 before the Letter is
// accepted, so the tab can call it for any carrier.
export const useCommitmentReadiness = (
  organizationId?: number,
  options: { enabled?: boolean } = {}
) =>
  useQuery(
    ['ghg-supplier', 'commitment-target', 'readiness', { organizationId }],
    () =>
      client
        .get<CommitmentReadinessSchema>(
          `/web/ghg-suppliers/${organizationId}/commitment-target/readiness`
        )
        .then((res) => res.data),
    { enabled: organizationId !== undefined && (options.enabled ?? true) }
  );

// C10 -- per-country aggregates (C08, `GET .../survey/summary-by-country`)
// for the "Podsumowanie per kraj" card. Row/totals shape mirrors
// CountryAggregateSummarySchema (backend/ghg_suppliers/api/
// api_summary_country.py) exactly -- every number here is a server value,
// never re-derived or re-summed client-side. In particular `totals` can
// legitimately be LESS than the sum of `rows[].suppliers`
// (`totals_note.double_counted_rows`): a survey spanning two countries
// counts once per country row but only once, distinct, in `totals` -- do
// not "fix" that by summing rows.
export interface GhgCountryAggregateRow {
  country_code: string | null;
  country_name: string | null;
  branch_count: number;
  suppliers: number;
  registered: number;
  survey_complete: number;
  scope12_complete: number;
}

export interface GhgCountryAggregateTotals {
  suppliers: number;
  registered: number;
  survey_complete: number;
  scope12_complete: number;
}

export interface GhgCountryAggregateSummary {
  rows: GhgCountryAggregateRow[];
  totals: GhgCountryAggregateTotals;
  totals_note: { double_counted_rows: boolean };
}

export const useGhgSupplierSurveySummaryByCountry = (
  organizationId?: number,
  fromPeriod?: string,
  toPeriod?: string,
  branches?: string[],
  countries?: string[],
  search?: string
) => {
  const endpointUrl = `/web/ghg-suppliers/${organizationId}/survey/summary-by-country`;
  const query = useQuery(
    [
      'ghg-supplier',
      'survey-summary-by-country',
      { organizationId, fromPeriod, toPeriod, branches, countries, search },
    ],
    () =>
      client
        .get<GhgCountryAggregateSummary>(endpointUrl, {
          params: {
            from_period: fromPeriod,
            to_period: toPeriod,
            branches,
            countries,
            search,
          },
        })
        .then((response) => response.data),
    { enabled: !!organizationId && !!fromPeriod && !!toPeriod }
  );

  return query;
};

// C10 -- paginated/filterable survey summary (C06 Interface/Contract §1b)
// backing the SupplierSummary table: `page/page_size/search/countries` on
// `GET .../survey/summary`. Deliberately a NEW hook rather than widening
// Scope3GoodsAndServices/hooks.tsx's useSupplierSurvey (outside this
// component's Affected Files, and shared by SupplierSurveys/CopyLink,
// which must keep behaving exactly as they do today). Reuses that file's
// already-exported envelope-unwrapping so both hooks normalize the
// bare-array/{items,total,page,page_size} duality the same way; `total` is
// null when `page` wasn't requested (SupplierSummary's "view all" mode,
// which -- per that same backward-compat contract -- omits `page` entirely
// rather than sending page_size=-1, which the backend would reject).
// `organizationIds` (RG 28.08 / E-S4) is the server-side "selected companies"
// filter -- see getOrganizationIdsParam in Scope3GoodsAndServices/hooks.tsx.
export const useGhgSupplierSurveySummary = (
  organizationId?: number,
  fromPeriod?: string,
  toPeriod?: string,
  branches?: string[],
  countries?: string[],
  search?: string,
  page?: number,
  pageSize?: number,
  organizationIds?: number[]
) => {
  const endpointUrl = `/web/ghg-suppliers/${organizationId}/survey/summary`;
  const query = useQuery(
    [
      'ghg-supplier',
      'survey-summary-paged',
      {
        organizationId,
        fromPeriod,
        toPeriod,
        branches,
        countries,
        search,
        page,
        pageSize,
        organizationIds,
      },
    ],
    () =>
      requestSurveys<
        | GhgSupplierSurveySummaryWithCountry[]
        | GhgSupplierSurveyPagedResult<GhgSupplierSurveySummaryWithCountry>
      >(
        endpointUrl,
        {
          from_period: fromPeriod,
          to_period: toPeriod,
          branches,
          organization_ids: organizationIds,
          countries,
          search,
          page,
          page_size: pageSize,
        },
        // "Selected companies" mode: a long id list goes in a POST body, not
        // in the URL -- see requestSurveys.
        organizationIds !== undefined
      ).then((response) => response.data),
    { enabled: !!organizationId && !!fromPeriod && !!toPeriod }
  );

  const { items, total } = unwrapSurveyList(query.data);

  return { ...query, items, total };
};
